// ProductNewsBell — cái chuông «Tin mới» trên thanh trên cùng (và đáy rail khi màn hẹp / giao diện
// cũ). DÙNG CHUNG Core + Work: tin soạn ở Core, Core đọc bảng gốc, Work đọc bản đồng bộ — chuông
// không cần biết mình đang ở hệ nào, `@host/axios` đã trỏ đúng máy chủ.
//
// Khuôn bảng (theo mẫu sếp Phú gửi 29/09): tiêu đề · 2 tab «Tất cả / Chưa đọc N» · nút «Đánh dấu
// đã đọc» · danh sách nhóm theo ngày, mỗi dòng có chủ đề, tiêu đề, đoạn tóm tắt, giờ đăng, chấm
// đỏ khi chưa đọc · chân bảng «Xem tất cả» sang trang /whats-new. Bấm một dòng = mở bài ngay tại
// chỗ (popup), không rời trang đang làm.
//
// Đây KHÔNG phải kênh báo tin: báo tin là việc của bot «Thông báo» trong chat (luật «mọi thông
// báo đi bằng bot»). Chuông chỉ là nơi ĐỌC LẠI, có số chưa đọc để khách không bỏ lỡ.
//
// Thêm 12/10/2026 (sếp Phú): yêu cầu hỗ trợ khách gửi CNV (khung «Trợ giúp AI») có cập nhật — CNV
// trả lời / đổi trạng thái — cũng hiện ở đây, trộn theo thời gian với tin; bấm là mở khung hỗ trợ
// đúng yêu cầu đó. Dữ liệu lấy từ chính widget (supportTicketFeed.js); host không nhúng widget thì
// phần này rỗng, chuông y như cũ.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CheckCircle2, ChevronRight, Loader2, Sparkles, TrendingUp, Wrench, Megaphone, LifeBuoy,
} from 'lucide-react';
import {
  useProductNews, refreshProductNews, markAllProductNewsRead, openProductNews,
} from './productNewsStore';
import {
  useSupportTickets, initSupportTicketFeed, refreshSupportTickets, openSupportTicket, markAllSupportTicketsSeen,
} from './supportTicketFeed';
import { OVER_WIDGET_Z } from '../support-help/helpWidget';
import { kindMeta } from './kinds';
import { fmtDate, fmtDateTime } from '../../i18n/format.js';

import { useLingui } from '@lingui/react/macro';

const PANEL_W = 420;

// Chỉ nhập ĐÚNG 4 icon chủ đề — chuông nằm ở bundle đầu trang, `import * as LucideIcons` sẽ kéo
// cả nghìn icon vào chunk chính.
const KIND_ICONS = { Sparkles, TrendingUp, Wrench, Megaphone };
const REFRESH_MS = 5 * 60 * 1000;

/** Khoá ngày theo giờ VN để nhóm dòng — trùng múi giờ với fmtDate (xem i18n/format.js). */
function dayKey(iso) {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit', day: '2-digit' })
      .format(new Date(iso));
  } catch { return ''; }
}

/**
 * Chỗ đặt bảng: nút nằm nửa PHẢI màn hình (thanh trên cùng) ⇒ thả xuống, canh mép phải theo nút;
 * nút ở rail TRÁI ⇒ mở sang phải, canh ĐÁY theo nút (nút nằm ở đáy rail).
 * Đo rect TRƯỚC khi đổi state (React 18 xả state ngay trong handler — [[feedback_react18_measure_rect_before_closing_menu]]).
 */
function placePanel(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(PANEL_W, vw - 16);
  if (!rect) return { top: 56, right: 8, width, maxHeight: vh - 72 };
  if (rect.left > vw / 2) {
    const top = rect.bottom + 8;
    const left = Math.max(8, Math.min(rect.right - width, vw - width - 8));
    return { top, left, width, maxHeight: Math.max(240, vh - top - 16) };
  }
  const left = Math.min(rect.right + 8, vw - width - 8);
  const bottom = Math.max(8, vh - rect.bottom);
  return { bottom, left, width, maxHeight: Math.max(240, vh - bottom - 16) };
}

/** Màu nhãn trạng thái yêu cầu — cùng ba tông widget dùng (open | wait | done). */
const TICKET_TONE = {
  open: 'bg-sky-50 text-sky-700 border-sky-200',
  wait: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function KindIcon({ kind, cover }) {
  if (cover) {
    return <img src={cover} alt="" className="w-11 h-11 rounded-xl object-cover bg-gray-100 shrink-0" />;
  }
  const k = kindMeta(kind);
  const Icon = KIND_ICONS[k.icon] || Bell;
  return (
    <span className={`w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 ${k.chip}`}>
      <Icon size={18} />
    </span>
  );
}

/**
 * @param {'topbar'|'rail'} props.variant  topbar = nút trên thanh v2 (class .v2-topbar__icon);
 *                                         rail = nút ở đáy rail tối (màn < 1024px, giao diện v1)
 */
export default function ProductNewsBell({ variant = 'topbar' }) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { items, unread: newsUnread, loaded, loading, error } = useProductNews();
  const tk = useSupportTickets();
  const unread = newsUnread + tk.unread;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  // Nạp lúc vào trang + mỗi 5 phút + khi quay lại tab (kho tự chặn gọi dồn dưới 30 giây).
  useEffect(() => {
    initSupportTicketFeed();
    refreshProductNews();
    refreshSupportTickets();
    const iv = setInterval(() => { refreshProductNews(); refreshSupportTickets(); }, REFRESH_MS);
    const onFocus = () => { refreshProductNews(); refreshSupportTickets(); };
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, []);

  // Đóng khi bấm ra ngoài / Esc / đổi cỡ cửa sổ.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Mở lại bảng thì luôn bắt đầu ở tab «Tất cả».
  useLayoutEffect(() => { if (!open) setTab('all'); }, [open]);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const rect = btnRef.current?.getBoundingClientRect() || null;
    setPos(placePanel(rect));
    setOpen(true);
    refreshProductNews({ force: true });
    refreshSupportTickets({ force: true });
  };

  // Tin + yêu cầu hỗ trợ có cập nhật, trộn theo thời gian (mới nhất trước).
  const list = useMemo(() => {
    const rows = [
      ...items.map((it) => ({ type: 'news', key: `n:${it.id}`, at: it.published_at, unread: !it.is_read, it })),
      ...tk.items.map((tkt) => ({ type: 'ticket', key: `t:${tkt.id}`, at: tkt.at, unread: tkt.has_news, tkt })),
    ].sort((a, b) => (Date.parse(b.at) || 0) - (Date.parse(a.at) || 0));
    return tab === 'unread' ? rows.filter((r) => r.unread) : rows;
  }, [items, tk.items, tab]);

  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const r of list) {
      const k = dayKey(r.at);
      if (!cur || cur.key !== k) { cur = { key: k, date: r.at, rows: [] }; out.push(cur); }
      cur.rows.push(r);
    }
    return out;
  }, [list]);

  const todayKey = dayKey(new Date().toISOString());
  const badge = unread > 9 ? '9+' : String(unread);
  const label = unread > 0 ? t`Tin mới — ${unread} bài chưa đọc` : t`Tin mới`;

  const openRow = (it) => {
    setOpen(false);
    openProductNews(it.id, it);
  };

  const openTicketRow = (tkt) => {
    setOpen(false);
    openSupportTicket(tkt.id);
  };

  const markAllRead = () => {
    if (newsUnread > 0) markAllProductNewsRead();
    if (tk.unread > 0) markAllSupportTicketsSeen();
  };

  const button = variant === 'rail' ? (
    <button ref={btnRef} type="button" onClick={toggle} title={label} aria-label={label}
            className="relative w-10 h-10 mx-auto rounded-xl flex items-center justify-center text-slate-300 hover:bg-white/5 hover:text-white transition">
      <Bell size={18} />
      {unread > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-[16px] text-center shadow-md ring-1 ring-slate-900/60">
          {badge}
        </span>
      )}
    </button>
  ) : (
    <button ref={btnRef} type="button" className="v2-topbar__icon" onClick={toggle} title={label} aria-label={label}>
      <Bell size={16} />
      {unread > 0 && <span className="v2-topbar__dot" aria-hidden="true">{badge}</span>}
    </button>
  );

  return (
    <>
      {button}
      {open && pos && createPortal(
        <div ref={panelRef} role="dialog" aria-label={t`Tin mới`}
             className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden text-gray-800"
             style={{ top: pos.top, bottom: pos.bottom, left: pos.left, right: pos.right, width: pos.width, maxHeight: pos.maxHeight, zIndex: OVER_WIDGET_Z }}>
          <div className="px-5 pt-4 pb-3 border-b border-gray-100">
            <div className="text-base font-bold text-gray-900">{t`Tin mới`}</div>
            <div className="text-xs text-gray-500 mt-0.5">{t`Tính năng mới, cải tiến và thông báo từ CNV`}</div>
          </div>

          <div className="px-4 py-2.5 flex items-center justify-between gap-2 border-b border-gray-100">
            <div className="inline-flex p-0.5 rounded-xl bg-gray-100 text-sm">
              <button type="button" onClick={() => setTab('all')}
                      className={`px-3 py-1 rounded-lg ${tab === 'all' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {t`Tất cả`}
              </button>
              <button type="button" onClick={() => setTab('unread')}
                      className={`px-3 py-1 rounded-lg ${tab === 'unread' ? 'bg-white shadow-sm font-semibold text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                {t`Chưa đọc`}{unread > 0 && <span className="ml-1 text-rose-600 font-semibold">{unread}</span>}
              </button>
            </div>
            <button type="button" onClick={markAllRead} disabled={unread === 0}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-default">
              <CheckCircle2 size={14} /> {t`Đánh dấu đã đọc`}
            </button>
          </div>

          <div className="overflow-y-auto flex-1 min-h-0">
            {!loaded && loading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-400">
                <Loader2 size={16} className="animate-spin" /> {t`Đang tải…`}
              </div>
            ) : list.length === 0 ? (
              <div className="py-12 px-6 text-center text-sm text-gray-400">
                {error && !items.length && !tk.items.length
                  ? t`Chưa tải được tin mới. Thử lại sau ít phút.`
                  : tab === 'unread' ? t`Bạn đã đọc hết tin mới.` : t`Chưa có tin mới nào.`}
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.key}>
                  <div className="flex items-center gap-3 px-5 pt-3 pb-1">
                    <span className="h-px flex-1 bg-gray-100" />
                    <span className="text-[11px] font-medium text-gray-400">
                      {g.key === todayKey ? t`Hôm nay` : fmtDate(g.date)}
                    </span>
                    <span className="h-px flex-1 bg-gray-100" />
                  </div>
                  {g.rows.map((r) => {
                    if (r.type === 'ticket') {
                      const tkt = r.tkt;
                      const who = tkt.by || t`Đội hỗ trợ CNV`;
                      return (
                        <button key={r.key} type="button" onClick={() => openTicketRow(tkt)}
                                className="w-full text-left px-5 py-3 flex gap-3 hover:bg-gray-50 transition-colors">
                          <span className="w-11 h-11 rounded-xl border flex items-center justify-center shrink-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                            <LifeBuoy size={18} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-2">
                              <span className="shrink-0 inline-flex px-1.5 py-px rounded-md border text-[10.5px] font-medium bg-emerald-50 text-emerald-700 border-emerald-200">{t`Yêu cầu hỗ trợ`}</span>
                              <span className="text-[11px] text-gray-400 truncate">{fmtDateTime(tkt.at)}</span>
                            </span>
                            <span className={`mt-1 block text-sm leading-snug line-clamp-2 ${r.unread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                              {tkt.code ? `${tkt.code} · ` : ''}{tkt.title}
                            </span>
                            {tkt.kind === 'reply' && tkt.excerpt ? (
                              <span className="mt-0.5 block text-[13px] leading-snug text-gray-500 line-clamp-2">
                                {t`${who} trả lời: ${tkt.excerpt}`}
                              </span>
                            ) : tkt.status_label ? (
                              <span className="mt-1 flex items-center gap-1.5 text-[12px] text-gray-500">
                                {t`Trạng thái:`}
                                <span className={`inline-flex px-1.5 py-px rounded-md border text-[11px] font-medium ${TICKET_TONE[tkt.status_tone] || TICKET_TONE.open}`}>{tkt.status_label}</span>
                              </span>
                            ) : null}
                            {!tkt.mine && tkt.requester_name ? (
                              <span className="mt-0.5 block text-[11px] text-gray-400 truncate">{t`Gửi bởi ${tkt.requester_name}`}</span>
                            ) : null}
                          </span>
                          {r.unread && <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" aria-label={t`Chưa đọc`} />}
                        </button>
                      );
                    }
                    const it = r.it;
                    const k = kindMeta(it.kind);
                    return (
                      <button key={r.key} type="button" onClick={() => openRow(it)}
                              className="w-full text-left px-5 py-3 flex gap-3 hover:bg-gray-50 transition-colors">
                        <KindIcon kind={it.kind} cover={it.cover_image_url} />
                        <span className="flex-1 min-w-0">
                          <span className="flex items-center gap-2">
                            <span className={`shrink-0 inline-flex px-1.5 py-px rounded-md border text-[10.5px] font-medium ${k.chip}`}>{k.label}</span>
                            <span className="text-[11px] text-gray-400 truncate">{fmtDateTime(it.published_at)}</span>
                          </span>
                          <span className={`mt-1 block text-sm leading-snug line-clamp-2 ${it.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                            {it.title}
                          </span>
                          {it.excerpt ? (
                            <span className="mt-0.5 block text-[13px] leading-snug text-gray-500 line-clamp-2">{it.excerpt}</span>
                          ) : null}
                        </span>
                        {!it.is_read && <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-500 shrink-0" aria-label={t`Chưa đọc`} />}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <button type="button" onClick={() => { setOpen(false); navigate('/whats-new'); }}
                  className="px-5 py-3 border-t border-gray-100 text-sm font-medium text-blue-600 hover:bg-blue-50/60 inline-flex items-center justify-center gap-1">
            {t`Xem tất cả tin mới`} <ChevronRight size={16} />
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}
